import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { VTableHeaderType } from "@app/directives/_layout/v-table/_enums/VTableHeaderType.enum";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableHeader } from "@app/directives/_layout/v-table/_models/IVTableHeader";
import { IVTableHeaderOptions } from "@app/directives/_layout/v-table/_models/IVTableHeaderOptions";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { ResourceType } from "@app/enum/ResourceType";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { ReportTableRowDataSource } from "@app/main/components/reports/enums/ReportTableRowDataSource";
import { Colour } from "@app/main/components/reports/models/IDrivingStyleMetricScore";
import { IDataDefinition, IReportDefinitionHistory } from "@app/main/components/reports/models/IReportDefinition";
import { IReportRow } from "@app/main/components/reports/models/IReportRow";
import { IMetaDataResourceInformation, IReportRowCell } from "@app/main/components/reports/models/IReportRowCell";
import { IDrivingStyleMetricScore } from "@app/main/components/resourceManagement/driving-styles-manager/driving-styles.interfaces";
import { DateService } from "@app/services/date.service";
import { UnitConversionService } from "@app/services/unitConversion.service";
import { ColumnDataType } from "../../enums/ColumnDataType";
import { IColumnHeader } from "../../models/IColumnHeader";
import { DashboardService } from "@app/main/modules/dashboard/dashboard.service";
import copy from "fast-copy";
import './report-table.component.scss';

@Component({
    selector: 'v-report-table',
    template: require('./report-table.component.html')
})
export class VReportTableComponent {
    @Input() report: IDataDefinition;
    @Input() maxRows: number;
    @Input() clickable: boolean = true;
    @Input() showSearch: boolean;
    @Input() loading: boolean;
    @Input() exportTitle: string;
    @Input() hideFilterBar: boolean;

    @Output() rowClicked = new EventEmitter<IVTableRow>();

    //--

    tableColumns: IVTableColumn<IReportRow>[];
    tableRows: IVTableRow[];

    isActualReport: boolean;

    showFilterBar: boolean;

    constructor(
        private readonly el: ElementRef,
        private readonly dateService: DateService,
        private readonly vTableService: VTableService,
        private readonly dashboardService: DashboardService
    ) { }

    ngOnInit(): void {
        this.dashboardService.showFilterBar$.subscribe(value => {
            this.showFilterBar = value;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.report && changes["report"]) {
            this.isActualReport = this.report["ReportType"] != undefined;

            this.createTableColumns();

            this.completeWhenFinishedAnimating(null, changes["report"]?.isFirstChange()).then(() => {
                this.createTableRows();
            });
        }
    }

    onRowClicked(row: IVTableRow): void {
        if (!this.clickable) {
            return;
        }

        this.rowClicked.emit(row);
    }

    //--

    private completeWhenFinishedAnimating(previousReading: number = null, showLoadingIndicator = false): Promise<any> {

        var currentTableWidth = this.getTableWidth();
        if (previousReading != null && currentTableWidth == previousReading) {
            if (showLoadingIndicator) {
                this.loading = false;
            }

            return Promise.resolve();
        }

        if (showLoadingIndicator) {
            this.loading = true;
        }

        return Promise.resolve(setTimeout(() => {
            return this.completeWhenFinishedAnimating(currentTableWidth, showLoadingIndicator);
        }, 50));
    }

    private createTableColumns(): void {
        this.tableColumns = [];

        this.report.TableDefinition.Headers.forEach(header => {
            if (!header || header.Title == "Row Title") {
                if (this.isActualReport) {
                    return;
                }
            }

            const tableHeader = this.createTableHeader(header);

            if (header.ColumnDefinition.Type == ColumnDataType.Score) {
                //tableHeader.width = 100;
                tableHeader.sortDirection = SortDirection.Ascending;
            }

            // Reset any sort already applied
            if (tableHeader.sorted) {
                this.tableColumns.forEach(c => c.header.sorted = false);
            }

            this.tableColumns.push({
                header: tableHeader,
                createCellFunction: () => null
            })
        })
    }

    private createTableRows(): void {
        this.tableRows = [];

        const tableDefinition = copy(this.report.TableDefinition);

        if (!tableDefinition.Rows) {
            return;
        }

        tableDefinition.Rows.forEach((row, rowIndex) => {
            const newTableRow: IVTableRow = {
                id: rowIndex,
                data: row,
                cells: [],
                searchString: row.Cells?.map(c => c.Contents?.toString()?.toLowerCase()).join(" ") ?? ""
            }

            row.Cells.forEach((cell, cellIndex) => {
                newTableRow.cells.push(this.createCellFromReportRowCell(cell, cellIndex, rowIndex))
            })

            this.tableRows.push(newTableRow)
        })
    }

    private getTableWidth(): number {
        return this.el.nativeElement.getBoundingClientRect().width;
    }

    private createTableHeader(header: IColumnHeader): IVTableHeader {
        const isFirstColumn = header.Title == "Row Title";

        const headerOptions: IVTableHeaderOptions = {
            title: isFirstColumn ? ReportTableRowDataSource[this.report.RowDataSource] : header.Title,
            sortable: true,
            sortType: header.SortType,
            sorted: header.Sorted,
            classes: "multi-line",
            measurementCategory: header.ColumnDefinition.MeasurementCategory
        };

        const specificWidth = this.getWidthForColumn(header);
        if (specificWidth) {
            headerOptions.width = specificWidth;
        }

        if (isFirstColumn && this.report.RowDataSource == ReportTableRowDataSource.Journey) {
            headerOptions.width = 100;
        }

        if (isFirstColumn) {
            switch (this.report.RowDataSource) {
                case ReportTableRowDataSource.Vehicle:
                    return this.vTableService.createVehicleTableHeader(headerOptions);

                case ReportTableRowDataSource.Day:
                    return this.vTableService.createDateTableHeader(headerOptions);
            };
        }

        if (header.ColumnDefinition.Type == ColumnDataType.Date || header.ColumnDefinition.Type == ColumnDataType.Day) {
            return this.vTableService.createDateTableHeader(headerOptions);
        }

        return this.vTableService.createTableHeader(headerOptions);
    }

    private getWidthForColumn(header: IColumnHeader): number {
        switch (header.ColumnDefinition.Type) {
            case ColumnDataType.Score:
            case ColumnDataType.Int:
            case ColumnDataType.Double:
            case ColumnDataType.Percent:
                return 100;
        }

        return null;
    }

    private createCellFromReportRowCell(cell: IReportRowCell, cellIndex: number, rowIndex: number): IVTableCell {
        const cellHeader: IVTableHeader = this.tableColumns[cellIndex].header;

        if (cellHeader.sortType == SortType.Date) {
            return this.vTableService.createDateCell(cell.Data);
        }

        if (this.report.RowDataSource === ReportTableRowDataSource.Journey && cellIndex === 0) {
            cell.Data = rowIndex + 1;
            cell.Contents = `#${cell.Data}`;
        }

        const vTableRowCell: IVTableCell = this.vTableService.createTableCell(this.formatContents(cell, cellHeader), {
            sortData: cell.Data,
            exportData: cell.Contents
        })

        if (cell.MetaData) {
            const metricScore = cell.MetaData as IDrivingStyleMetricScore;
            const resourceInformation = cell.MetaData as IMetaDataResourceInformation;

            if (resourceInformation && resourceInformation.ResourceId) {
                switch (resourceInformation.ResourceType) {
                    case ResourceType.Vehicle:
                        return this.vTableService.createVehicleRegCell(resourceInformation.Resource);
                }
            }

            if (!!metricScore.Colour) {
                return this.createScoreCell(cell);
            }
        }

        return vTableRowCell;
    }

    private createScoreCell(reportRowCell: IReportRowCell): IVTableCell {
        const metricScore = reportRowCell?.MetaData as IDrivingStyleMetricScore;

        if (!metricScore) {
            return this.vTableService.createTableCell(null);
        }

        return this.vTableService.createTableCell(reportRowCell.Contents, {
            sortData: reportRowCell.Data,
            exportData: Math.round(reportRowCell.Data).toString(),
            classes: `cell-colour-${Colour[metricScore.Colour].toLowerCase()} driver-score`
        })
    }

    private formatContents(cell: IReportRowCell<any>, header: IVTableHeader): string {
        if (cell.DataSourceValue !== "RowTitle" || this.report.RowDataSource !== ReportTableRowDataSource.Day) {
            return cell.Contents;
        }

        if (header.type == VTableHeaderType.Date) {
            const dataDate = new Date(cell.Data);

            if (!isNaN(new Date(dataDate).getTime())) {
                return this.dateService.formatDate(dataDate)
            }
        }

        return cell.Contents;
    }
}
